package com.kinoshazam.screens.history

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.kinoshazam.uiKit.components.image.ImageUiKitState
import com.kinoshazam.uiKit.components.image.UiKitImage
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.uiKit.theme.smallRadius
import features.watch.model.WatchEntry
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.back
import kinoshazam.resources.generated.resources.delete_from_history
import kinoshazam.resources.generated.resources.film_label
import kinoshazam.resources.generated.resources.history_earlier
import kinoshazam.resources.generated.resources.history_empty
import kinoshazam.resources.generated.resources.history_this_week
import kinoshazam.resources.generated.resources.history_today
import kinoshazam.resources.generated.resources.history_yesterday
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.season_episode
import kinoshazam.resources.generated.resources.watch_history
import kinoshazam.resources.generated.resources.watched_mark
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import presentation.screens.history.HistoryBucket
import presentation.screens.history.HistoryGroup
import presentation.screens.history.HistoryMVIAction
import presentation.screens.history.HistoryViewModel
import utils.FormFactor

private val DesktopContentMaxWidth = 1200.dp

@Composable
fun HistoryContentHost(formFactor: FormFactor) {
    val viewModel = koinViewModel<HistoryViewModel>()
    val state by viewModel.state.collectAsStateWithLifecycle()
    val wide = formFactor is FormFactor.Expanded || formFactor is FormFactor.ExtraLarge
    if (wide) {
        WideHistoryScreen(state.groups, viewModel::onAction)
    } else {
        CompactHistoryScreen(state.groups, viewModel::onAction)
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CompactHistoryScreen(
    groups: List<HistoryGroup>,
    onAction: (HistoryMVIAction) -> Unit,
) {
    Scaffold(
        topBar = {
            TopAppBar(
                modifier = Modifier.statusBarsPadding(),
                title = {
                    TextLabel(
                        state = TextLabelState(
                            text = stringResource(Res.string.watch_history),
                            color = UiKitTheme.colors.color_2,
                            textStyle = BoldLarge,
                        ),
                    )
                },
                navigationIcon = {
                    Image(
                        painter = painterResource(Res.drawable.back),
                        colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                        contentDescription = null,
                        modifier = Modifier
                            .padding(horizontal = 16.dp)
                            .clickable { onAction(HistoryMVIAction.Back) },
                    )
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = UiKitTheme.colors.color_3,
                ),
            )
        },
        containerColor = UiKitTheme.colors.color_3,
    ) { padding ->
        HistoryList(
            groups = groups,
            wide = false,
            onAction = onAction,
            modifier = Modifier.padding(padding),
        )
    }
}

@Composable
private fun WideHistoryScreen(
    groups: List<HistoryGroup>,
    onAction: (HistoryMVIAction) -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(UiKitTheme.colors.color_3),
    ) {
        Column(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .widthIn(max = DesktopContentMaxWidth)
                .fillMaxWidth()
                .fillMaxSize()
                .padding(start = 60.dp, end = 60.dp, top = 40.dp, bottom = 90.dp),
        ) {
            WideTopBar(
                title = stringResource(Res.string.watch_history),
                onBack = { onAction(HistoryMVIAction.Back) },
            )
            Spacer(Modifier.size(32.dp))
            HistoryList(
                groups = groups,
                wide = true,
                onAction = onAction,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

@Composable
private fun WideTopBar(
    title: String,
    onBack: () -> Unit,
) {
    Box(modifier = Modifier.fillMaxWidth()) {
        Image(
            painter = painterResource(Res.drawable.back),
            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
            contentDescription = null,
            modifier = Modifier
                .align(Alignment.CenterStart)
                .clickable(onClick = onBack),
        )
        TextLabel(
            state = TextLabelState(
                text = title,
                color = UiKitTheme.colors.color_2,
                textStyle = BoldLarge,
                textAlign = TextAlign.Center,
                maxLines = 1,
            ),
            modifier = Modifier
                .align(Alignment.Center)
                .padding(horizontal = 72.dp),
        )
    }
}

@Composable
private fun HistoryList(
    groups: List<HistoryGroup>,
    wide: Boolean,
    onAction: (HistoryMVIAction) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (groups.isEmpty()) {
        Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Text(
                text = stringResource(Res.string.history_empty),
                color = UiKitTheme.colors.color_2,
                style = Medium,
                modifier = Modifier.padding(24.dp),
            )
        }
        return
    }
    LazyColumn(
        modifier = modifier.fillMaxWidth(),
        contentPadding = PaddingValues(bottom = 24.dp),
    ) {
        groups.forEach { group ->
            item(key = group.bucket.name) {
                Text(
                    text = bucketTitle(group.bucket),
                    color = UiKitTheme.colors.color_2,
                    style = BoldMedium,
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp),
                )
            }
            items(
                items = group.items,
                key = { "${group.bucket}:${it.videoId}:${it.seasonNumber}:${it.episodeNumber}" },
            ) { entry ->
                HistoryRow(
                    entry = entry,
                    wide = wide,
                    onOpen = { onAction(HistoryMVIAction.Open(entry)) },
                    onDelete = { onAction(HistoryMVIAction.Delete(entry)) },
                )
            }
        }
    }
}

@Composable
private fun HistoryRow(
    entry: WatchEntry,
    wide: Boolean,
    onOpen: () -> Unit,
    onDelete: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onOpen)
            .padding(horizontal = 16.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        UiKitImage(
            state = ImageUiKitState(
                url = if (wide) entry.backdropUrl ?: entry.imageUrl else entry.imageUrl,
                placeholder = painterResource(Res.drawable.movie),
                contentScale = ContentScale.Crop,
            ),
            modifier = Modifier
                .width(if (wide) 168.dp else 56.dp)
                .height(if (wide) 94.dp else 84.dp)
                .clip(smallRadius),
        )
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(horizontal = 12.dp),
        ) {
            Text(
                text = entry.title.orEmpty(),
                color = UiKitTheme.colors.color_2,
                style = BoldMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                text = historySubtitle(entry),
                color = UiKitTheme.colors.color_11,
                style = Medium,
                maxLines = 1,
            )
            if (entry.status != WatchEntry.STATUS_COMPLETED && entry.progress > 0.0) {
                Spacer(Modifier.height(6.dp))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(3.dp)
                        .background(UiKitTheme.colors.color_5),
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth(entry.progress.toFloat().coerceIn(0f, 1f))
                            .height(3.dp)
                            .background(UiKitTheme.colors.color_1),
                    )
                }
            }
        }
        Text(
            text = stringResource(Res.string.delete_from_history),
            color = UiKitTheme.colors.color_1,
            style = Medium,
            modifier = Modifier.clickable(onClick = onDelete),
        )
    }
}

@Composable
private fun historySubtitle(entry: WatchEntry): String {
    val kind = if (entry.isTvShow && (entry.seasonNumber > 0 || entry.episodeNumber > 0)) {
        stringResource(Res.string.season_episode, entry.seasonNumber, entry.episodeNumber)
    } else {
        stringResource(Res.string.film_label)
    }
    return if (entry.status == WatchEntry.STATUS_COMPLETED) {
        "$kind · ${stringResource(Res.string.watched_mark)}"
    } else {
        kind
    }
}

@Composable
private fun bucketTitle(bucket: HistoryBucket): String = when (bucket) {
    HistoryBucket.TODAY -> stringResource(Res.string.history_today)
    HistoryBucket.YESTERDAY -> stringResource(Res.string.history_yesterday)
    HistoryBucket.WEEK -> stringResource(Res.string.history_this_week)
    HistoryBucket.EARLIER -> stringResource(Res.string.history_earlier)
}
